import { useMemo, useState, type FunctionComponent } from "react";
import { FireOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import dayjs from "dayjs";

import {
  HabitCompletionStatus,
  HabitStatus,
  HabitType,
  type HabitSchedule,
} from "@fitness-app/data-models/entities/Habit";
import {
  calculateHabitCompletionRate,
  habitScheduledDatesInRange,
} from "@fitness-app/data-models/domain/services/habitCompliance";

import { habitTypeColors, habitTypeLabels } from "~/modules/Habits/constants";
import { formatSchedule } from "~/modules/Habits/helpers";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";
import AssignHabitModal from "./AssignHabitModal";
import HabitComplianceCalendar from "./HabitComplianceCalendar";

type ClientHabitRow = {
  id: string;
  templateId: string;
  status: string;
  currentStreak: number;
  longestStreak: number;
  startDate: string | null;
  lastCompletedDate: string | null;
  template: {
    name: string;
    type: HabitType;
    schedule: HabitSchedule | null;
  } | null;
};

type CompletionRow = {
  clientHabitId: string;
  date: string;
  status: string;
};

const habitStatusLabels: Record<string, string> = {
  [HabitStatus.Active]: "Aktywny",
  [HabitStatus.Inactive]: "Nieaktywny",
  [HabitStatus.Upcoming]: "Nadchodzący",
};

const habitStatusColors: Record<string, string> = {
  [HabitStatus.Active]: "green",
  [HabitStatus.Inactive]: "default",
  [HabitStatus.Upcoming]: "blue",
};

type Props = {
  traineeId: string;
  userId: string | null;
};

const TraineeHabitsSection: FunctionComponent<Props> = ({ traineeId, userId }) => {
  const queryClient = useQueryClient();
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const habitsQuery = useQuery({
    queryKey: challengesHabitsKeys.habits(traineeId),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_habit")
        .select(
          "id, templateId, status, currentStreak, longestStreak, startDate, lastCompletedDate, template:templateId(name, type, schedule)",
        )
        .eq("traineeId", traineeId)
        .order("createdAt", { ascending: false })
        .returns<ClientHabitRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const since = dayjs().subtract(6, "day").format("YYYY-MM-DD");

  const completionsQuery = useQuery({
    queryKey: challengesHabitsKeys.habitCompletions(traineeId, since),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_completion")
        .select("clientHabitId, date, status")
        .eq("traineeId", traineeId)
        .gte("date", since)
        .returns<CompletionRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  // TRE-1579 — "last 7 days" per habit, measured against the habit's SCHEDULE, not calendar days:
  // the denominator counts only days the habit was actually planned (per template.schedule, and not
  // before the habit's startDate), and the numerator counts scheduled days with a completed/partial
  // entry. A 3×/week habit at full compliance now reads 3/3, not 3/7.
  const last7ByHabit = useMemo(() => {
    const doneDatesByHabit = new Map<string, string[]>();
    for (const completion of completionsQuery.data ?? []) {
      if (completion.status === HabitCompletionStatus.Skipped) {
        continue;
      }
      const list = doneDatesByHabit.get(completion.clientHabitId) ?? [];
      list.push(completion.date);
      doneDatesByHabit.set(completion.clientHabitId, list);
    }

    const today = dayjs().format("YYYY-MM-DD");
    const map = new Map<string, { done: number; scheduled: number }>();

    for (const row of habitsQuery.data ?? []) {
      // Shared domain math — the same helpers the mobile detail sheet uses.
      const scheduledDates = habitScheduledDatesInRange({
        schedule: row.template?.schedule ?? null,
        startDate: row.startDate,
        from: since,
        to: today,
      });
      const { completed, scheduled } = calculateHabitCompletionRate(scheduledDates, doneDatesByHabit.get(row.id) ?? []);
      map.set(row.id, { done: completed, scheduled });
    }

    return map;
  }, [completionsQuery.data, habitsQuery.data, since]);

  const deleteMutation = useMutation({
    mutationFn: async (clientHabitId: string) => {
      const { error } = await supabase.from("client_habit").delete().eq("id", clientHabitId);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Usunięto");
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.habits(traineeId) });
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się usunąć: ${error.message}`);
    },
  });

  const assignedTemplateIds = useMemo(() => (habitsQuery.data ?? []).map((row) => row.templateId), [habitsQuery.data]);

  return (
    <Card
      title="Nawyki i zadania"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAssignOpen(true)}>
          Przypisz nawyk / zadanie
        </Button>
      }
    >
      <Table<ClientHabitRow>
        dataSource={habitsQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={habitsQuery.isFetching}
        locale={{ emptyText: "Brak przypisanych nawyków i zadań" }}
        pagination={false}
        expandable={{
          // Lazy per-habit compliance calendar — the query inside only fires on expand.
          expandedRowRender: (row) => (
            <HabitComplianceCalendar
              clientHabitId={row.id}
              schedule={row.template?.schedule ?? null}
              startDate={row.startDate}
              longestStreak={row.longestStreak}
            />
          ),
        }}
      >
        <Table.Column<ClientHabitRow>
          title="Nazwa"
          key="name"
          render={(_, row) => <Typography.Text strong>{row.template?.name ?? "-"}</Typography.Text>}
        />
        <Table.Column<ClientHabitRow>
          title="Typ"
          key="type"
          render={(_, row) =>
            row.template?.type ? (
              <Tag color={habitTypeColors[row.template.type] ?? "default"}>
                {habitTypeLabels[row.template.type] ?? row.template.type}
              </Tag>
            ) : (
              "-"
            )
          }
        />
        <Table.Column<ClientHabitRow>
          title="Harmonogram"
          key="schedule"
          render={(_, row) => formatSchedule(row.template?.schedule)}
        />
        <Table.Column<ClientHabitRow>
          title="Status"
          dataIndex="status"
          key="status"
          render={(status: string) => (
            <Tag color={habitStatusColors[status] ?? "default"}>{habitStatusLabels[status] ?? status}</Tag>
          )}
        />
        <Table.Column<ClientHabitRow>
          title="Seria"
          key="streak"
          render={(_, row) =>
            // TRE-1656 — tasks are once-and-done: streaks don't apply.
            row.template?.type === HabitType.Task ? (
              <Typography.Text type="secondary">—</Typography.Text>
            ) : (
              <Tooltip title={`Najdłuższa seria: ${row.longestStreak}`}>
                <Space size={4}>
                  <FireOutlined style={{ color: "#fa541c" }} />
                  <Typography.Text>
                    {row.currentStreak} / {row.longestStreak}
                  </Typography.Text>
                </Space>
              </Tooltip>
            )
          }
        />
        <Table.Column<ClientHabitRow>
          title="Ostatnie 7 dni"
          key="last7"
          render={(_, row) => {
            const stats = last7ByHabit.get(row.id);
            if (!stats || stats.scheduled === 0) {
              return <Typography.Text type="secondary">brak zaplanowanych</Typography.Text>;
            }
            return (
              <Tooltip title="Wykonane / zaplanowane wg harmonogramu">
                <Typography.Text
                  type={stats.done === stats.scheduled ? "success" : undefined}
                >{`${stats.done}/${stats.scheduled}`}</Typography.Text>
              </Tooltip>
            );
          }}
        />
        <Table.Column<ClientHabitRow>
          title="Ostatnio wykonane"
          dataIndex="lastCompletedDate"
          key="lastCompletedDate"
          render={(date: string | null) => (date ? dayjs(date).format("DD.MM.YYYY") : "-")}
        />
        <Table.Column<ClientHabitRow>
          title="Opcje"
          key="action"
          render={(_, row) => (
            <Popconfirm
              title="Usunąć przypisanie?"
              okText="Usuń"
              cancelText="Anuluj"
              okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
              onConfirm={() => deleteMutation.mutate(row.id)}
            >
              <Button type="link" danger className="px-0">
                Usuń
              </Button>
            </Popconfirm>
          )}
        />
      </Table>

      <AssignHabitModal
        open={isAssignOpen}
        traineeId={traineeId}
        userId={userId}
        assignedTemplateIds={assignedTemplateIds}
        onClose={() => setIsAssignOpen(false)}
      />
    </Card>
  );
};

export default TraineeHabitsSection;
